import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Brand, getBrandByUserId, getMatchesForBrand } from '../../services/dataService';
import { Match } from '../../services/matchingService';
import { Button } from '../../components/Button';
import { ProfileSummary } from '../../components/dashboard/ProfileSummary';
import { MiniMetric } from '../../components/dashboard/MiniMetric';
import { MatchCard } from '../../components/dashboard/MatchCard';
import { ActivityItem } from '../../components/dashboard/ActivityFeed';
export function BrandDashboard() {
  const {
    currentUser
  } = useAuth();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadData = async () => {
      if (currentUser) {
        const brandData = getBrandByUserId(currentUser.id);
        setBrand(brandData);
        if (brandData) {
          const matchData = getMatchesForBrand(brandData.id);
          setMatches(matchData);
        }
      }
      setLoading(false);
    };
    loadData();
  }, [currentUser]);
  if (loading) {
    return <DashboardLayout userType="brand" data-id="element-1966">
        <div className="flex justify-center items-center h-64" data-id="element-1967">
          <div className="text-gray-500" data-id="element-1968">Loading...</div>
        </div>
      </DashboardLayout>;
  }
  if (!brand) {
    return <DashboardLayout userType="brand" data-id="element-1969">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1970">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1971">
            Complete Your Profile
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-1972">
            You need to complete your brand profile before you can start
            matching with event organizers.
          </p>
          <Link to="/onboarding/brand" data-id="element-1973">
            <Button variant="primary" data-id="element-1974">Complete Profile</Button>
          </Link>
        </div>
      </DashboardLayout>;
  }
  // Calculate stats
  const pendingMatches = matches.filter(m => m.status === 'pending');
  const acceptedMatches = matches.filter(m => m.status === 'accepted');
  const avgMatchScore = matches.length > 0 ? Math.round(matches.reduce((acc, m) => acc + m.score, 0) / matches.length) : 0;
  return <DashboardLayout userType="brand" data-id="element-1975">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1976">
        {/* Left sidebar */}
        <aside className="lg:col-span-1 bg-white rounded-2xl p-4 shadow-sm h-fit" data-id="element-1977">
          <ProfileSummary view="brand" name={brand.companyName} stats={{
          matches: matches.length,
          openDeals: pendingMatches.length,
          activeCampaigns: acceptedMatches.length,
          avgMatch: `${avgMatchScore}%`
        }} data-id="element-1978" />
        </aside>

        {/* Main content */}
        <section className="lg:col-span-3 space-y-6" data-id="element-1979">
          {/* Brand Overview */}
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-1980">
            <div className="flex items-start justify-between" data-id="element-1981">
              <div data-id="element-1982">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-1983">
                  Brand Overview
                </h2>
                <p className="text-sm text-gray-500" data-id="element-1984">
                  Quick snapshot of your sponsorship activity and suggested
                  events.
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-1985">
                <Link to="/onboarding/brand" className="text-sm px-3 py-1 border rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1986">
                  Edit profile
                </Link>
                <Link to="/dashboard/matches" className="text-sm px-3 py-1 bg-violet-600 text-white rounded-md hover:bg-violet-700" data-id="element-1987">
                  Find Events
                </Link>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1988">
              <MiniMetric title="Budget Range" value={brand.budget === 'under_1000' ? '< $1k' : brand.budget === '1000_5000' ? '$1k - $5k' : brand.budget === '5000_10000' ? '$5k - $10k' : brand.budget === '10000_25000' ? '$10k - $25k' : '$25k+'} data-id="element-1989" />
              <MiniMetric title="Goals" value={brand.marketingGoals || 'Awareness'} data-id="element-1990" />
              <MiniMetric title="Industry" value={brand.industry ? brand.industry.charAt(0).toUpperCase() + brand.industry.slice(1).replace('_', ' ') : 'General'} data-id="element-1991" />
            </div>
          </section>

          {/* Top Matching Events */}
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-1992">
            <div className="flex items-center justify-between mb-4" data-id="element-1993">
              <div data-id="element-1994">
                <h3 className="text-md font-semibold text-gray-900" data-id="element-1995">
                  Top Matching Events
                </h3>
                <p className="text-sm text-gray-500" data-id="element-1996">
                  Based on your preferences
                </p>
              </div>
              <Link to="/dashboard/matches" className="text-sm text-violet-600 hover:text-violet-800 font-medium" data-id="element-1997">
                View all
              </Link>
            </div>
            <div className="space-y-3" data-id="element-1998">
              {matches.length === 0 ? <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200" data-id="element-1999">
                  No matches found yet. Complete your profile to get matched
                  with relevant events.
                </div> : matches.slice(0, 3).map(match => <MatchRowWrapper key={match.id} match={match} data-id="element-2000" />)}
            </div>
          </section>

          {/* Active Campaigns */}
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-2001">
            <div className="flex items-center justify-between mb-3" data-id="element-2002">
              <h3 className="text-md font-semibold text-gray-900" data-id="element-2003">
                Active Campaigns
              </h3>
              <div className="text-sm text-gray-500" data-id="element-2004">
                {acceptedMatches.length} live
              </div>
            </div>
            <div className="space-y-3" data-id="element-2005">
              {acceptedMatches.length === 0 ? <div className="text-sm text-gray-500 italic" data-id="element-2006">
                  No active campaigns yet. Connect with organizers to start!
                </div> : acceptedMatches.slice(0, 2).map(match => <ActivityItemWrapper key={`active-${match.id}`} match={match} data-id="element-2007" />)}
            </div>
          </section>
        </section>
      </div>
    </DashboardLayout>;
}
// Wrapper components
function MatchRowWrapper({
  match
}: {
  match: Match;
}) {
  const [organizer, setOrganizer] = useState<any>(null);
  useEffect(() => {
    const organizerData = JSON.parse(localStorage.getItem('organizers') || '[]');
    const found = organizerData.find((o: any) => o.id === match.organizerId);
    setOrganizer(found || null);
  }, [match]);
  if (!organizer) return null;
  return <MatchCard id={match.id} name={organizer.eventName} sub={organizer.location} desc={organizer.elevatorPitch || 'No description available'} matchScore={match.score} status={match.status} view="brand" data-id="element-2008" />;
}
function ActivityItemWrapper({
  match
}: {
  match: Match;
}) {
  const [organizer, setOrganizer] = useState<any>(null);
  useEffect(() => {
    const organizerData = JSON.parse(localStorage.getItem('organizers') || '[]');
    const found = organizerData.find((o: any) => o.id === match.organizerId);
    setOrganizer(found || null);
  }, [match]);
  if (!organizer) return null;
  return <ActivityItem title={organizer.eventName} subtitle={`Matched on ${new Date(match.createdAt).toLocaleDateString()}`} status="Active" type="campaign" data-id="element-2009" />;
}